// CSR-2055 — the catalogue of ACORD forms the "Generate ACORD Form" flow can
// produce (CSR-1824).
//
// Shared so the selection modal, the API layer, and the server-side fill service
// all read one list and cannot disagree about what a form is or what it needs.
// This module describes forms only; which AMS value lands in which PDF blank is
// owned by the per-form mapping tickets (CSR-2058..CSR-2061), and the fill
// service deliberately knows nothing about either.
import { normalizeCarrierName } from "./carrier-name";
import { formatMonthDayYear } from "./policy-date";

// Every policy number selected goes on the form's single policy line,
// comma-separated (confirmed by Ana on CSR-1824) — "multi" never means one PDF
// per policy. ACORD 36 is the exception with seven real slots, hence
// `maxPolicies`.
/**
 * CSR-2062 — gates the whole "Generate ACORD Form" surface. Default OFF.
 *
 * The flow spans several tickets (this modal, the editor in CSR-2063, the
 * per-form field maps in CSR-2058..CSR-2061), and a half-built flow must not be
 * reachable in production: with the flag OFF the Documents tab looks exactly as
 * it does today. Flipping it ON per environment is what launches the feature,
 * and flipping it back is the kill switch.
 *
 * Declared here rather than mirrored as a string literal on each side, because
 * the client's gate and the server's seed row have to name the SAME flag — two
 * copies drift and the gate silently opens (or never does).
 */
export const FLAG_GENERATE_ACORD_FORM = "feature_flag_generate_acord_form";

/**
 * CSR-2064 — how a saved ACORD form is recorded in `activity_logs`.
 *
 * The form is logged ONCE, keyed to the document it produced, with every policy
 * it covers listed in `details.policyIds`. The policy-scoped feed finds that
 * single row from each of those policies by array containment, so a
 * multi-policy form reaches every linked policy's Activities tab without the
 * write fanning out a row per policy — which would render one action as several
 * cards, against the Event Logging spec's "one fact, one card".
 *
 * Shared rather than written as literals at each site because three places have
 * to agree on it: the save endpoint that writes the row, the policy feed's SQL
 * that matches it, and the Policy Activities tab that renders it. If any two
 * disagreed the event would simply never appear, with nothing to show it had
 * been lost.
 */
export const ACORD_GENERATED_LOG = {
  entityType: "insured_document",
  action: "generated",
} as const;

export type AcordPolicySelection = "none" | "single" | "multi";

// Values the agent supplies in the selection modal that come from neither the
// insured nor the policy. Both current cases are dates.
export interface AcordExtraField {
  key: "cancellationEffectiveDate" | "cancellationDate";
  label: string;
  type: "date";
}

export interface AcordFormDefinition {
  id: AcordFormId;
  label: string;
  // CSR-2057 — the name the SAVED document carries, and the title the editor
  // modal shows: "[Form Type] — [ACORD Number]", per the design spec.
  // Deliberately a separate string from `label`, which the selection modal lists
  // the other way round ("ACORD 35 — Cancellation Request"). Deriving one from
  // the other by splitting on the em dash breaks the day a label has none.
  documentName: string;
  // File under `server/assets/`, resolved via `resolveAssetPath()`. Null while a
  // form's PDF has not been supplied.
  templateFile: string | null;
  policySelection: AcordPolicySelection;
  // Whether a policy MUST be chosen before the form can be generated. Only
  // ACORD 35 requires one; everywhere else the agent may skip the selection and
  // leave the policy blanks empty (design spec, "Generate button state").
  policyRequired: boolean;
  // Hard slot count on the paper, where one exists. Absent means the selected
  // numbers share one comma-separated line. CSR-2059: a selection longer than the
  // slot count is REFUSED with a 400 naming the limit, not trimmed to fit — see
  // `selectAcordPolicies`.
  maxPolicies?: number;
  // CSR-2331 — every selected UNDERLYING policy must have the same carrier AND
  // the same effective date; anything else is a separate document. The umbrella
  // itself is exempt (see `isAcordUmbrellaPolicyType`). Set where the paper
  // prints one carrier and one term per row but a row can hold several policy
  // numbers: there, a policy on a mixed row reads as written by the other
  // policy's carrier. The modal disables a policy that would break it and
  // `selectAcordPolicies` refuses one with a 400, both through
  // `acordPolicyMismatches`. Absent means the selection is not constrained.
  policiesMustShareCarrierAndEffectiveDate?: boolean;
  extraFields: readonly AcordExtraField[];
  // False while a form cannot be generated at all, so the selection modal can
  // leave it out. Bill of Sale is blocked on CSR-2067 — its PDF was never
  // supplied, so its policy mode and extra fields are still unknown.
  available: boolean;
}

export type AcordFormId =
  | "acord-125"
  | "bill-of-sale"
  | "acord-35"
  | "acord-37"
  | "acord-126"
  | "rps-cargo-app"
  | "acord-36"
  | "acord-127"
  | "acord-131"
  | "w-9";

// Order matches the design spec's form list, which is the order the selection
// modal renders.
export const ACORD_FORMS: readonly AcordFormDefinition[] = [
  {
    id: "acord-125",
    label: "ACORD 125 — Commercial Insurance Application",
    documentName: "Commercial Insurance Application — ACORD 125",
    templateFile: "acord-125.pdf",
    policySelection: "multi",
    policyRequired: false,
    extraFields: [],
    available: true,
  },
  {
    id: "bill-of-sale",
    label: "Bill of Sale",
    documentName: "Bill of Sale",
    templateFile: null,
    policySelection: "none",
    policyRequired: false,
    extraFields: [],
    available: false,
  },
  {
    id: "acord-35",
    label: "ACORD 35 — Cancellation Request",
    documentName: "Cancellation Request — ACORD 35",
    templateFile: "acord-35.pdf",
    policySelection: "single",
    policyRequired: true,
    extraFields: [
      {
        key: "cancellationEffectiveDate",
        label: "Effective Date of Cancellation",
        type: "date",
      },
    ],
    available: true,
  },
  {
    id: "acord-37",
    label: "ACORD 37 — Statement of No Loss",
    documentName: "Statement of No Loss — ACORD 37",
    templateFile: "acord-37.pdf",
    policySelection: "multi",
    policyRequired: false,
    extraFields: [{ key: "cancellationDate", label: "Cancellation Date", type: "date" }],
    available: true,
  },
  {
    id: "acord-126",
    label: "ACORD 126 — Commercial General Liability",
    documentName: "Commercial General Liability — ACORD 126",
    templateFile: "acord-126.pdf",
    policySelection: "multi",
    policyRequired: false,
    extraFields: [],
    available: true,
  },
  {
    id: "rps-cargo-app",
    label: "RPS Cargo App",
    documentName: "RPS Cargo App",
    templateFile: "rps-cargo-app.pdf",
    policySelection: "none",
    policyRequired: false,
    extraFields: [],
    available: true,
  },
  {
    id: "acord-36",
    label: "ACORD 36 — Agent / Broker of Record Change",
    documentName: "Agent / Broker of Record Change — ACORD 36",
    templateFile: "acord-36.pdf",
    policySelection: "multi",
    policyRequired: false,
    // The paper has seven policy slots, and they are the authorization itself —
    // an eighth policy is refused rather than dropped (CSR-2059).
    maxPolicies: 7,
    extraFields: [],
    available: true,
  },
  {
    id: "acord-127",
    label: "ACORD 127 — Business Auto Section",
    documentName: "Business Auto Section — ACORD 127",
    templateFile: "acord-127.pdf",
    policySelection: "multi",
    policyRequired: false,
    extraFields: [],
    available: true,
  },
  {
    id: "acord-131",
    label: "ACORD 131 — Umbrella / Excess Section",
    documentName: "Umbrella / Excess Section — ACORD 131",
    templateFile: "acord-131.pdf",
    policySelection: "multi",
    policyRequired: false,
    // Its UNDERLYING INSURANCE rows print one carrier and one term above a
    // policy-number line that every policy of that line shares.
    policiesMustShareCarrierAndEffectiveDate: true,
    extraFields: [],
    available: true,
  },
  {
    id: "w-9",
    label: "W-9",
    documentName: "W-9",
    templateFile: "w-9.pdf",
    policySelection: "none",
    policyRequired: false,
    extraFields: [],
    available: true,
  },
] as const;

// Characters that cannot appear in a stored filename or a Content-Disposition
// header. The em dash in every ACORD document name is deliberately NOT replaced
// — it is valid in both, and the spec's product-facing name should survive.
const FILENAME_UNSAFE = /[\\/:*?"<>|]/g;

/**
 * The filename a generated document carries.
 *
 * CSR-2063 — shared rather than server-only, because the Download button names
 * the file the browser saves and the save endpoint names the row in the Uploaded
 * table. Two copies of this rule would let the same document arrive under two
 * names.
 */
export function acordDocumentFileName(form: AcordFormDefinition): string {
  return `${form.documentName.replace(FILENAME_UNSAFE, "_")}.pdf`;
}

export function findAcordForm(id: string): AcordFormDefinition | undefined {
  return ACORD_FORMS.find((form) => form.id === id);
}

// The two facts `policiesMustShareCarrierAndEffectiveDate` compares, as both the
// client's policy rows and the server's `AcordContextPolicy` carry them.
export interface AcordPolicyTerms {
  carrier: string | null;
  effectiveDate: string | null;
}

export interface AcordPolicyMismatch {
  field: "carrier" | "effectiveDate";
  // The candidate has no value on record at all, rather than a different one.
  notOnRecord: boolean;
}

// The COI's carrier rule (CSR-780), so "Progressive Mountain Insurance Co" and
// "PROGRESSIVE MOUNTAIN INSCO" are one carrier here exactly as they are one
// insurer letter on the insured's certificate.
function carrierKey(policy: AcordPolicyTerms): string {
  return normalizeCarrierName(policy.carrier ?? "");
}

// Compared as the form prints it: two policies whose effective dates print
// identically ARE one effective date for the paper, which is the property the
// rule protects. `formatMonthDayYear` is also string-to-string, so a timezone
// can never move a date onto the next day; anything it cannot read is "".
function effectiveDayKey(policy: AcordPolicyTerms): string {
  return formatMonthDayYear(policy.effectiveDate);
}

/**
 * CSR-2331 — whether a policy is the umbrella an ACORD 131 is written for.
 *
 * The umbrella fills the form's header, not an UNDERLYING INSURANCE row, so the
 * one-carrier, one-effective-date rule never applies to it: it is usually written
 * by a different carrier, on a different term, than the policies beneath it (Ana,
 * CSR-2331). Shared because the modal, the server's refusal and the field map
 * all have to agree on which policy that is.
 */
export function isAcordUmbrellaPolicyType(policyType: string | null | undefined): boolean {
  return (policyType ?? "").trim().toLowerCase() === "umbrella";
}

/**
 * CSR-2331 — how `candidate` differs from a policy already selected, or `[]` when
 * the two may share one form.
 *
 * The ONE comparison behind `policiesMustShareCarrierAndEffectiveDate`: the
 * modal disables an option with it and the server refuses a request with it, so
 * the two cannot disagree about which selections are allowed.
 *
 * A missing carrier or effective date matches nothing — not even another missing
 * one. Two policies with no carrier on record are not known to share a carrier,
 * and treating them as one would put a carrier the agent types in against both
 * (the COI's carrier key keeps carrier-less policies apart for the same reason).
 * Such a policy still generates on a form of its own.
 */
export function acordPolicyMismatches(
  selected: AcordPolicyTerms,
  candidate: AcordPolicyTerms,
): AcordPolicyMismatch[] {
  const mismatches: AcordPolicyMismatch[] = [];
  const fields = [
    ["carrier", carrierKey],
    ["effectiveDate", effectiveDayKey],
  ] as const;
  for (const [field, key] of fields) {
    const candidateValue = key(candidate);
    if (candidateValue === "") {
      mismatches.push({ field, notOnRecord: true });
    } else if (key(selected) !== candidateValue) {
      mismatches.push({ field, notOnRecord: false });
    }
  }
  return mismatches;
}

/**
 * CSR-2123 — a blank's name with the template's own naming style stripped off.
 *
 * ACORD 35/36/37/131 name their blanks hierarchically
 * (`F[0].P1[0].NamedInsured_FullName_A[0]`) and 125/126/127 flat
 * (`NamedInsured_FullName_A`), so the SAME blank on two forms has two names.
 * Reduce both to `NamedInsured_FullName_A` and a rule about a blank can be
 * written once instead of per template.
 *
 * Use this only where the rule really is about the blank rather than the form —
 * the per-form field maps deliberately spell names out in full, because which
 * value belongs in which blank does NOT transfer between forms.
 */
export function acordBlankBaseName(fieldName: string): string {
  return fieldName.replace(/^F\[\d+\]\.P\d+\[\d+\]\./, "").replace(/\[\d+\]$/, "");
}

/**
 * The line the agency's authorized representative signs.
 *
 * Present on ACORD 35, 125, 126, 127 and 131. ACORD 36 and 37 have no such line — the
 * signatures they carry are the INSURED's and a witness's, which we must never
 * sign — and the W-9 and RPS Cargo App are signed by the applicant. Naming the
 * blank once, here, is what lets the fill service state the rule as "wherever a
 * template has this line, our representative signs it" instead of keeping a list
 * of forms in step with the templates.
 */
export const ACORD_PRODUCER_SIGNATURE_BLANK = "Producer_AuthorizedRepresentative_Signature_A";

/**
 * "PRODUCER'S NAME (Please Print)" — the printed counterpart of the signature.
 *
 * The ACORD 125 family and ACORD 131 print it; ACORD 35 pairs its signature line
 * with a date instead. ACORD 37 has a blank of this name too, but no producer
 * signature beside it, so it is never filled there.
 */
export const ACORD_PRODUCER_SIGNATURE_NAME_BLANK =
  "Producer_AuthorizedRepresentative_FullName_A";

/**
 * CSR-2123 — the two blanks that say who signed, which the SERVER fills and the
 * agent cannot.
 *
 * They have to move together. The signature is a fixed one (`Daniel Summers`,
 * hardcoded since CSR-671), so leaving the printed name beside it editable would
 * let a form go out reading "signed by X" above X's colleague's signature — a
 * document that contradicts itself about who authorised it. Neither is offered as
 * an input in the editor, and neither is taken from the request body: a value the
 * client cannot legitimately send is one the server must not honour, whether it
 * arrives from a crafted request or from a browser still running yesterday's
 * bundle.
 *
 * The day representatives get their own signatures (CSR-671's open "source of
 * truth" question), both values come from that one identity — which is exactly
 * why they are one set here rather than two decisions.
 */
const SERVER_OWNED_BLANKS: readonly string[] = [
  ACORD_PRODUCER_SIGNATURE_BLANK,
  ACORD_PRODUCER_SIGNATURE_NAME_BLANK,
];

export function isAcordServerOwnedBlank(fieldName: string): boolean {
  return SERVER_OWNED_BLANKS.includes(acordBlankBaseName(fieldName));
}

// Forms the agent may actually generate — everything with a supplied template.
export function generatableAcordForms(): readonly AcordFormDefinition[] {
  return ACORD_FORMS.filter((form) => form.available);
}
